/**
 * Page texture (snippets/page-texture.liquid).
 *
 * - Grain: every element in the main content painted in the page's own gray (and with no image of its own) gets
 *   .tx-grain, which lays the grain over it. Colors never change, so sections, cards and toggles all still match.
 * - Glow: the first heading of each section gets .tx-glow, a soft red light behind it, but not card or product
 *   titles, and not in the sections that have their own look.
 *
 * Performance notes (same look as before, much less work for the browser):
 * - Styles are all read first and the classes are all added afterwards. Reading a style right after adding a class
 *   makes the browser redo its style work each time; batching means it does that work once per pass.
 * - Parts that can never be a gray surface (icon drawings, images, scripts, form fields) are skipped.
 * - Follow-up checks run when the browser is idle, so they never hold up scrolling, tapping or loading.
 * - Only new content, and elements whose classes change, are rechecked; inline style changes from animations are
 *   ignored.
 */

/** Sections that keep their own look */
const OWN_LOOK = '.hero-slides-section, .sell-trade-section';

/**
 * Headings that get a glow: the first h2/h3 that isn't inside a card or the product details.
 * <body> is left out of the card check because its class names the theme's card hover effect.
 */
const HEADING =
  ':is(h2, h3):not([class*="card"]:not(body) *, product-card *, .product-details *, [aria-hidden="true"] *)';

/** Elements that can never be a painted gray surface, so they're never checked */
const SKIP =
  'svg, svg *, img, picture, source, video, iframe, canvas, script, style, link, template, noscript, br, input, select, textarea, option';

/** Run work when the browser has a spare moment (or within a second at the latest) */
const idle = (/** @type {() => void} */ callback) =>
  'requestIdleCallback' in window ? window.requestIdleCallback(callback, { timeout: 1000 }) : window.setTimeout(callback, 50);

/**
 * Elements under (and including) root that still need checking.
 * @param {Element} root
 * @param {Set<Element>} into
 */
function collect(root, into) {
  if (!(root instanceof Element) || root.matches(SKIP)) return;
  if (!root.classList.contains('tx-grain')) into.add(root);
  for (const element of root.querySelectorAll('*')) {
    if (!element.classList.contains('tx-grain') && !element.matches(SKIP)) into.add(element);
  }
}

/**
 * Gives .tx-grain to every element in the set that's painted in the page's gray. All reads, then all writes.
 * @param {Iterable<Element>} elements
 */
function applyGrain(elements) {
  const pageColor = getComputedStyle(document.body).backgroundColor;
  /** @type {Element[]} */
  const matches = [];
  for (const element of elements) {
    if (!element.isConnected || element.classList.contains('tx-grain')) continue;
    const style = getComputedStyle(element);
    if (style.backgroundColor === pageColor && style.backgroundImage === 'none') matches.push(element);
  }
  for (const element of matches) element.classList.add('tx-grain');
}

/** @param {Element} main */
function scanAll(main) {
  /** @type {Set<Element>} */
  const elements = new Set();
  collect(main, elements);
  applyGrain(elements);
}

/** @param {Element} main */
function addGlows(main) {
  /** @type {[Element, Element][]} */
  const candidates = [];
  for (const section of main.querySelectorAll(':scope > .shopify-section')) {
    if (section.matches(OWN_LOOK) || section.querySelector('.tx-glow')) continue;
    const heading = section.querySelector(HEADING);
    if (!heading || !heading.textContent?.trim()) continue;
    candidates.push([section, heading]);
  }

  // Reads first, then writes
  const glows = candidates
    .filter(([, heading]) => getComputedStyle(heading, '::before').content === 'none')
    .map(([, heading]) => ({ heading, center: getComputedStyle(heading).textAlign === 'center' }));

  for (const { heading, center } of glows) {
    heading.classList.add('tx-glow');
    if (center) heading.classList.add('tx-glow--center');
  }
}

const main = document.getElementById('MainContent');
if (main) {
  scanAll(main);
  addGlows(main);

  // Some parts only get their colors a moment later (their stylesheet loads late, a script styles them), so the page
  // is looked over again in idle time: when stylesheets load, when the page has loaded, once more a couple of
  // seconds in, and when the page comes back from the browser's back/forward cache.
  let fullQueued = false;
  const recheck = () => {
    if (fullQueued) return;
    fullQueued = true;
    idle(() => {
      fullQueued = false;
      scanAll(main);
    });
  };
  document.addEventListener(
    'load',
    (event) => {
      if (event.target instanceof HTMLLinkElement && event.target.rel === 'stylesheet') recheck();
    },
    true
  );
  if (document.readyState === 'complete') recheck();
  else window.addEventListener('load', recheck, { once: true });
  window.setTimeout(recheck, 2500);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) recheck();
  });

  // Content added later (filters, sorting, more products, sections redrawn in place, the theme editor) and elements
  // whose classes change get the same treatment. Inline style changes (animations, sliders) are ignored.
  /** @type {Set<Element>} */
  const pending = new Set();
  let queued = false;
  let sectionsChanged = false;

  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') {
        const target = /** @type {Element} */ (record.target);
        if (!target.classList.contains('tx-grain') && !target.matches(SKIP)) pending.add(target);
        continue;
      }
      if (record.addedNodes.length > 0) sectionsChanged = true;
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) collect(node, pending);
      });
    }
    if (queued || pending.size === 0) return;
    queued = true;
    idle(() => {
      queued = false;
      applyGrain(pending);
      pending.clear();
      if (sectionsChanged) {
        sectionsChanged = false;
        addGlows(main);
      }
    });
  }).observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
}
